
import { motion } from "framer-motion";
import { Instagram, Youtube, Facebook, ArrowRight } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import { SOCIALS } from "@/lib/social";
import Blob from "@/components/ui/Blob";
import TikTokIcon from "@/components/ui/TikTokIcon";

export default function ConnectSection() {
  const theme = usePalette();
  const socials = [
    {
      platform: SOCIALS.instagram.platform,
      handle: SOCIALS.instagram.handle,
      url: SOCIALS.instagram.url,
      desc: "Daily inspo, stories, and reels",
      icon: <Instagram size={28} />,
      color: colors.berryRose,
      bg: colors.blushPink,
    },
    {
      platform: SOCIALS.tiktok.platform,
      handle: SOCIALS.tiktok.handle,
      url: SOCIALS.tiktok.url,
      desc: "Quick makes, trends, and fun",
      icon: <TikTokIcon className="w-7 h-7" />,
      color: colors.navy,
      bg: colors.softLilac,
    },
    {
      platform: SOCIALS.youtube.platform,
      handle: SOCIALS.youtube.handle,
      url: SOCIALS.youtube.url,
      desc: "Full tutorials and vlogs",
      icon: <Youtube size={28} />,
      color: colors.forestGreen,
      bg: colors.sageLight,
    },
    {
      platform: SOCIALS.facebook.platform,
      handle: SOCIALS.facebook.handle,
      url: SOCIALS.facebook.url,
      desc: "Community, updates, and live chats",
      icon: <Facebook size={28} />,
      color: colors.denimBlue,
      bg: colors.lilacLight,
    },
  ];

  return (
    <section
      id="connect"
      className="py-24 px-6 relative overflow-hidden"
      style={{ backgroundColor: colors.cream }}
    >
      <Blob className="w-80 h-80 bottom-0 right-0" color={colors.sage} />

      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeUp}
            custom={0}
            className="text-xs tracking-[0.2em] uppercase mb-3"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              color: theme.accent,
            }}
          >
            Let&apos;s Be Friends
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            className="text-4xl lg:text-5xl"
            style={{
              fontFamily: fonts.logo,
              fontWeight: 400,
              color: colors.navy,
            }}
          >
            Come Hang Out{" "}
            <span style={{ fontFamily: fonts.heading, color: theme.warm }}>
              With Me
            </span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          variants={stagger}
          className="grid grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {socials.map((s, i) => (
            <motion.a
              key={s.platform}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeUp}
              custom={i}
              className="group rounded-2xl p-6 cursor-pointer block"
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                style={{ background: s.bg, color: s.color }}
              >
                {s.icon}
              </div>
              <h4
                className="text-lg mb-1"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  color: colors.navy,
                }}
              >
                {s.platform}
              </h4>
              <p
                className="text-sm mb-1"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  color: s.color,
                }}
              >
                {s.handle}
              </p>
              <p
                className="text-xs"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 500,
                  color: `${colors.navy}77`,
                  letterSpacing: "0.03em",
                }}
              >
                {s.desc}
              </p>
              <div
                className="mt-4 inline-flex items-center gap-1 text-xs tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  color: theme.accent,
                }}
              >
                Follow
                <ArrowRight size={12} />
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
